import React, { useState, useRef, useEffect } from 'react'
import { Modal, Button, Slider } from 'antd'
import { MdZoomIn, MdZoomOut } from 'react-icons/md'
import './ImageCropperModal.scss'

const ImageCropperModal = ({
  visible,
  imageFile,
  aspectRatio,
  onCancel,
  onCrop
}) => {
  const [imageSrc, setImageSrc] = useState(null)
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [cropBox, setCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const [dragging, setDragging] = useState(false)
  const [resizing, setResizing] = useState(null)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(100)
  const [minZoom, setMinZoom] = useState(50)

  const imageRef = useRef(null)
  const containerRef = useRef(null)

  // Parse aspect ratio
  const getAspectRatioValue = () => {
    if (!aspectRatio) return 16 / 9
    const [width, height] = aspectRatio.split(':').map(Number)
    return width / height
  }

  const aspectRatioValue = getAspectRatioValue()

  // Load image from file
  useEffect(() => {
    if (imageFile && visible) {
      const reader = new FileReader()
      reader.onload = e => {
        setImageSrc(e.target.result)
      }
      reader.readAsDataURL(imageFile)
    }
  }, [imageFile, visible])

  // Initialize crop box when image loads
  const handleImageLoad = () => {
    if (!imageRef.current || !containerRef.current) return

    const img = imageRef.current
    const container = containerRef.current
    const containerRect = container.getBoundingClientRect()

    // Calculate displayed image size
    const imgAspect = img.naturalWidth / img.naturalHeight
    const containerAspect = containerRect.width / containerRect.height

    let displayWidth, displayHeight

    if (imgAspect > containerAspect) {
      displayWidth = containerRect.width
      displayHeight = displayWidth / imgAspect
    } else {
      displayHeight = containerRect.height
      displayWidth = displayHeight * imgAspect
    }

    setImageSize({ width: displayWidth, height: displayHeight })

    // Initialize crop box to center, 80% of image
    let cropWidth, cropHeight

    if (aspectRatioValue > displayWidth / displayHeight) {
      cropWidth = displayWidth * 0.8
      cropHeight = cropWidth / aspectRatioValue
    } else {
      cropHeight = displayHeight * 0.8
      cropWidth = cropHeight * aspectRatioValue
    }

    // Ensure crop box fits within image
    if (cropWidth > displayWidth) {
      cropWidth = displayWidth * 0.9
      cropHeight = cropWidth / aspectRatioValue
    }
    if (cropHeight > displayHeight) {
      cropHeight = displayHeight * 0.9
      cropWidth = cropHeight * aspectRatioValue
    }

    setCropBox({
      x: (displayWidth - cropWidth) / 2,
      y: (displayHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight
    })
  }

  // Handle crop box dragging
  const handleCropMouseDown = e => {
    if (e.target.classList.contains('crop-handle')) return
    e.preventDefault()
    setDragging(true)

    const container = containerRef.current
    const imageWrapper = container.querySelector('.image-wrapper')
    const rect = imageWrapper.getBoundingClientRect()

    setDragStart({
      x: e.clientX - rect.left - cropBox.x,
      y: e.clientY - rect.top - cropBox.y
    })
  }

  const handleMouseMove = e => {
    if (!containerRef.current) return

    const container = containerRef.current
    const imageWrapper = container.querySelector('.image-wrapper')
    if (!imageWrapper) return

    const rect = imageWrapper.getBoundingClientRect()

    if (dragging) {
      let newX = e.clientX - rect.left - dragStart.x
      let newY = e.clientY - rect.top - dragStart.y

      // Constrain to image bounds
      newX = Math.max(0, Math.min(newX, imageSize.width - cropBox.width))
      newY = Math.max(0, Math.min(newY, imageSize.height - cropBox.height))

      setCropBox(prev => ({ ...prev, x: newX, y: newY }))
    } else if (resizing) {
      handleResize(e)
    }
  }

  const handleMouseUp = () => {
    setDragging(false)
    setResizing(null)
  }

  // Handle corner resizing
  const handleResizeStart = (corner, e) => {
    e.stopPropagation()
    setResizing(corner)

    const container = containerRef.current
    const imageWrapper = container.querySelector('.image-wrapper')
    const rect = imageWrapper.getBoundingClientRect()

    setDragStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })
  }

  const handleResize = e => {
    if (!resizing || !imageRef.current || !containerRef.current) return

    const container = containerRef.current
    const imageWrapper = container.querySelector('.image-wrapper')
    if (!imageWrapper) return

    const rect = imageWrapper.getBoundingClientRect()

    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top

    const deltaX = mouseX - dragStart.x
    const deltaY = mouseY - dragStart.y

    let newBox = { ...cropBox }

    switch (resizing) {
      case 'nw':
        {
          const newWidth = cropBox.width - deltaX
          const newHeight = newWidth / aspectRatioValue
          const newX = cropBox.x + deltaX
          const newY = cropBox.y + (cropBox.height - newHeight)

          // Check bounds
          if (
            newWidth > 50 &&
            newWidth <= imageSize.width &&
            newHeight <= imageSize.height &&
            newX >= 0 &&
            newY >= 0
          ) {
            newBox = {
              x: newX,
              y: newY,
              width: newWidth,
              height: newHeight
            }
          }
        }
        break
      case 'ne':
        {
          const newWidth = cropBox.width + deltaX
          const newHeight = newWidth / aspectRatioValue
          const newY = cropBox.y + (cropBox.height - newHeight)

          // Check bounds
          if (
            newWidth > 50 &&
            cropBox.x + newWidth <= imageSize.width &&
            newHeight <= imageSize.height &&
            newY >= 0
          ) {
            newBox = {
              ...cropBox,
              y: newY,
              width: newWidth,
              height: newHeight
            }
          }
        }
        break
      case 'sw':
        {
          const newWidth = cropBox.width - deltaX
          const newHeight = newWidth / aspectRatioValue
          const newX = cropBox.x + deltaX

          // Check bounds
          if (
            newWidth > 50 &&
            newWidth <= imageSize.width &&
            newX >= 0 &&
            cropBox.y + newHeight <= imageSize.height
          ) {
            newBox = {
              ...cropBox,
              x: newX,
              width: newWidth,
              height: newHeight
            }
          }
        }
        break
      case 'se':
        {
          const newWidth = cropBox.width + deltaX
          const newHeight = newWidth / aspectRatioValue

          // Check bounds
          if (
            newWidth > 50 &&
            cropBox.x + newWidth <= imageSize.width &&
            cropBox.y + newHeight <= imageSize.height
          ) {
            newBox = {
              ...cropBox,
              width: newWidth,
              height: newHeight
            }
          }
        }
        break
    }

    setCropBox(newBox)
    setDragStart({ x: mouseX, y: mouseY })

    // Recalculate minimum zoom based on new crop box size
    const img = imageRef.current
    const containerRect = container.getBoundingClientRect()
    const imgAspect = img.naturalWidth / img.naturalHeight
    const containerAspect = containerRect.width / containerRect.height

    let baseWidth, baseHeight
    if (imgAspect > containerAspect) {
      baseWidth = containerRect.width
      baseHeight = baseWidth / imgAspect
    } else {
      baseHeight = containerRect.height
      baseWidth = baseHeight * imgAspect
    }

    const minZoomForWidth = (newBox.width / baseWidth) * 100
    const minZoomForHeight = (newBox.height / baseHeight) * 100
    const calculatedMinZoom = Math.max(minZoomForWidth, minZoomForHeight, 50)

    setMinZoom(calculatedMinZoom)

    // If current zoom is below minimum, adjust it
    if (zoom < calculatedMinZoom) {
      handleZoomChange(calculatedMinZoom)
    }
  }

  // Handle zoom
  const handleZoomChange = value => {
    if (!imageRef.current || !containerRef.current) return

    const img = imageRef.current
    const container = containerRef.current
    const containerRect = container.getBoundingClientRect()

    const imgAspect = img.naturalWidth / img.naturalHeight
    const containerAspect = containerRect.width / containerRect.height

    let baseWidth, baseHeight

    if (imgAspect > containerAspect) {
      baseWidth = containerRect.width
      baseHeight = baseWidth / imgAspect
    } else {
      baseHeight = containerRect.height
      baseWidth = baseHeight * imgAspect
    }

    // Calculate minimum zoom needed to contain crop box
    const minZoomForWidth = (cropBox.width / baseWidth) * 100
    const minZoomForHeight = (cropBox.height / baseHeight) * 100
    const calculatedMinZoom = Math.max(minZoomForWidth, minZoomForHeight, 50)

    // Update minimum zoom
    setMinZoom(calculatedMinZoom)

    // Constrain value to minimum zoom
    const constrainedValue = Math.max(value, calculatedMinZoom)
    setZoom(constrainedValue)

    const scale = constrainedValue / 100
    const newWidth = baseWidth * scale
    const newHeight = baseHeight * scale

    setImageSize({ width: newWidth, height: newHeight })

    // Adjust crop box to fit within new image bounds
    setCropBox(prev => {
      let newCropBox = { ...prev }

      // Constrain position to keep crop box within image bounds
      newCropBox.x = Math.max(
        0,
        Math.min(newCropBox.x, newWidth - newCropBox.width)
      )
      newCropBox.y = Math.max(
        0,
        Math.min(newCropBox.y, newHeight - newCropBox.height)
      )

      return newCropBox
    })
  }

  // Crop and export
  const handleCropConfirm = () => {
    if (!imageRef.current) return

    const img = imageRef.current
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    // Calculate scale from displayed size to natural size
    const scaleX = img.naturalWidth / imageSize.width
    const scaleY = img.naturalHeight / imageSize.height

    // Calculate crop coordinates in natural image size
    const sourceX = cropBox.x * scaleX
    const sourceY = cropBox.y * scaleY
    const sourceWidth = cropBox.width * scaleX
    const sourceHeight = cropBox.height * scaleY

    // Set output size (maintain good quality)
    canvas.width = Math.min(1920, sourceWidth)
    canvas.height = canvas.width / aspectRatioValue

    // Draw cropped image
    ctx.drawImage(
      img,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    )

    // Convert to blob and call onCrop
    canvas.toBlob(blob => {
      if (blob) {
        const croppedFile = new File([blob], imageFile.name, {
          type: imageFile.type
        })
        onCrop(croppedFile)
      }
    }, imageFile.type || 'image/png')
  }

  const handleReset = () => {
    setZoom(100)
    if (imageRef.current) {
      handleImageLoad()
    }
  }

  return (
    <Modal
      visible={visible}
      title={`Crop Image (${aspectRatio})`}
      onCancel={onCancel}
      width={920}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="crop" type="primary" onClick={handleCropConfirm}>
          Crop & Upload
        </Button>
      ]}
      className="image-cropper-modal"
      centered
      maskClosable={false}
    >
      <div
        className="cropper-container"
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          className="image-wrapper"
          style={{
            width: imageSize.width > 0 ? `${imageSize.width}px` : 'auto',
            height: imageSize.height > 0 ? `${imageSize.height}px` : 'auto'
          }}
        >
          <img
            ref={imageRef}
            src={imageSrc}
            alt="Crop preview"
            onLoad={handleImageLoad}
            draggable={false}
            style={{
              width: `${imageSize.width}px`,
              height: `${imageSize.height}px`
            }}
          />

          {cropBox.width > 0 && (
            <>
              {/* Crop box */}
              <div
                className="crop-box"
                style={{
                  left: cropBox.x,
                  top: cropBox.y,
                  width: cropBox.width,
                  height: cropBox.height
                }}
                onMouseDown={handleCropMouseDown}
              >
                {/* Grid lines */}
                <div className="crop-grid">
                  <div
                    className="grid-line grid-line-v"
                    style={{ left: '33.33%' }}
                  />
                  <div
                    className="grid-line grid-line-v"
                    style={{ left: '66.66%' }}
                  />
                  <div
                    className="grid-line grid-line-h"
                    style={{ top: '33.33%' }}
                  />
                  <div
                    className="grid-line grid-line-h"
                    style={{ top: '66.66%' }}
                  />
                </div>

                {/* Corner handles */}
                <div
                  className="crop-handle crop-handle-nw"
                  onMouseDown={e => handleResizeStart('nw', e)}
                />
                <div
                  className="crop-handle crop-handle-ne"
                  onMouseDown={e => handleResizeStart('ne', e)}
                />
                <div
                  className="crop-handle crop-handle-sw"
                  onMouseDown={e => handleResizeStart('sw', e)}
                />
                <div
                  className="crop-handle crop-handle-se"
                  onMouseDown={e => handleResizeStart('se', e)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="cropper-controls">
        <div className="control-group">
          <MdZoomOut className="control-icon" />
          <Slider
            min={minZoom}
            max={200}
            value={zoom}
            onChange={handleZoomChange}
            style={{ width: 200 }}
          />
          <MdZoomIn className="control-icon" />
          <span className="control-label">{Math.round(zoom)}%</span>
        </div>

        <Button size="small" onClick={handleReset}>
          Reset
        </Button>
      </div>
    </Modal>
  )
}

export default ImageCropperModal
